"use client";

import { isMobile } from "@/utils/helpers";
import { ReactNode, useEffect, useState } from "react";

interface ConfigurableMobileStickyNavProps {
  children: ReactNode;
  showOnScroll?: boolean;
  scrollThreshold?: number;
  topOffset?: number;
  zIndex?: number;
  className?: string;
  onVisibilityChange?: (isVisible: boolean) => void;
}

const ConfigurableMobileStickyNav: React.FC<ConfigurableMobileStickyNavProps> = ({
  children,
  showOnScroll = false,
  scrollThreshold = 200,
  topOffset = 0,
  zIndex = 40,
  className = "",
  onVisibilityChange
}) => {
  const [isVisible, setIsVisible] = useState(!showOnScroll);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll visibility and border state
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;

      // Update scrolled state for border styling
      setIsScrolled(scrollTop > topOffset);

      // Update visibility if showOnScroll is enabled
      if (showOnScroll) {
        const shouldBeVisible = scrollTop > scrollThreshold;
        if (shouldBeVisible !== isVisible) {
          setIsVisible(shouldBeVisible);
          onVisibilityChange?.(shouldBeVisible);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showOnScroll, scrollThreshold, topOffset, isVisible, onVisibilityChange]);

  if (!isMobile) return null;

  // Dynamic border class based on scroll position relative to nav position
  const borderClass = isScrolled ? 'border-b border-gray-300' : 'border-b border-transparent';

  const headerHeight = topOffset;

  return (
    <div
      className={`fixed left-0 right-0 bg-white transition-all duration-300 ease-in-out ${borderClass} ${className} md:hidden`}
      style={{
        top: headerHeight,
        zIndex,
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)'
      }}
    >
      {children}
    </div>
  );
};

export default ConfigurableMobileStickyNav;
